import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useHypoteseListe, useHypoteseTellinger, useNyeHypoteser, type HypoteseFilter, type HypothesisCounts } from '../api/useHypoteseBehandling.ts';
import { NyeStripe, RekkefolgeVelger, useRekkefolge } from '../innboks/Rekkefolge.tsx';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Ikon } from '../status/Ikon.tsx';
import { Arbeidsflate } from './Arbeidsflate.tsx';
import { Tekstknapp, Varsel } from './deler.tsx';
import { H_TIL_DEG } from './rommet.ts';

type Filter = 'alle' | 'avgjor' | 'venter';

/** «142», «H-142», «h 142» → 142. Noe annet er ikke et nummer (`HypothesisListQuery.number`). */
export function tolkNummer(tekst: string): number | null | undefined {
  const t = tekst.trim();
  if (!t) return undefined;
  const m = /^h?\s*-?\s*(\d{1,9})$/i.exec(t);
  if (!m) return null;
  const n = Number(m[1]);
  return n >= 1 ? n : null;
}

/**
 * Innboksen i rommet Hypoteser (DD-01 presisert 2026-09-26; `admin-hypoteser.jsx` SideInnboks):
 * behandlerens og eierens forside. Mottatt og Vurderes (`statusType = reviewing`), i tjenerens
 * rekkefølge — «Avgjør nå» først, så «Hva mister vi hvis vi venter?», så uten aktivitet, så eldst
 * (H6). Tre tall øverst, og alle tre er filtre (avvik 44): «til vurdering» (DD-52), «avgjør nå»
 * (parkert på sin dato er med her) og «venter på innsender». Et trykk på det valgte viser hele
 * innboksen igjen.
 *
 * Søket er nummeret. Kontrakten har ingen fritekst (`?number=` alene), så feltet heter «Nummer»,
 * ikke «Søk eller nummer» som i wireframen (rapportert som avvik).
 */
export function HInnboks({ nr }: { nr: number | null }) {
  const smal = useSmal();
  const [filter, setFilter] = useState<Filter>('alle');
  const [sok, setSok] = useState('');
  // Under 900 px er søket et ikon til det brukes (R4.4-grepet, DD-02); lukkes feltet, tømmes strengen.
  const [sokAapen, setSokAapen] = useState(false);
  const sokRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (sokAapen) sokRef.current?.focus();
  }, [sokAapen]);
  const aapneSok = useCallback(() => setSokAapen(true), []);
  const vekslSok = () => {
    if (sokAapen) setSok('');
    setSokAapen((v) => !v);
  };
  const tolket = tolkNummer(sok);
  // DD-74: «Frist først · Nyest først» i adressen, som i rommet Innspill. Gjelder ikke oppslaget.
  const [sort, setSort] = useRekkefolge();
  const spoerring = useMemo<HypoteseFilter>(() => {
    if (typeof tolket === 'number') return { number: String(tolket) };
    const base: HypoteseFilter = filter === 'avgjor' ? { dueNow: true } : filter === 'venter' ? { statusType: 'reviewing', awaitingSubmitter: true } : { statusType: 'reviewing' };
    return sort === 'nyest' ? { ...base, sort: 'nyest' } : base;
  }, [tolket, filter, sort]);
  const liste = useHypoteseListe(spoerring, tolket !== null);
  const tellinger = useHypoteseTellinger();
  const rader = useMemo(() => liste.data?.pages.flatMap((p) => p.items) ?? [], [liste.data]);
  const nye = useNyeHypoteser(spoerring, { rader, hentet: liste.dataUpdatedAt }, liste.isSuccess && tolket === undefined);
  // En tom innboks har ingenting som kan flytte seg: den nye hentes rett inn, uten stripe.
  const tomUtenFilter = filter === 'alle' && tolket === undefined && liste.isSuccess && rader.length === 0;
  const { refetch: hentListe } = liste;
  useEffect(() => {
    if (tomUtenFilter && nye.antall > 0) void hentListe();
  }, [tomUtenFilter, nye.antall, hentListe]);
  const c = tellinger.data;
  const tilVurdering = c ? c.received + c.reviewing : undefined;

  const tellere = c ? (
    <div role="group" aria-label="Filtre" className="hb-tellere" aria-live="polite">
      <TellerKnapp paa={filter === 'alle'} onClick={() => setFilter('alle')} tall={tilVurdering ?? 0} ord="til vurdering" />
      <TellerKnapp paa={filter === 'avgjor'} onClick={() => setFilter(filter === 'avgjor' ? 'alle' : 'avgjor')} tall={c.dueNow} ord="avgjør nå" varsel />
      <TellerKnapp paa={filter === 'venter'} onClick={() => setFilter(filter === 'venter' ? 'alle' : 'venter')} tall={c.awaitingSubmitter} ord="venter på innsender" />
    </div>
  ) : null;

  const sokFelt = (
    <label className="panel-sok hb-sok">
      <Ikon navn="search-outline" />
      <input ref={sokRef} id="hb-sok" type="search" value={sok} onChange={(e) => setSok(e.target.value)} placeholder="H-142" aria-label="Nummer" aria-keyshortcuts="/" inputMode="numeric" data-testid="hb-sok" />
      {sok ? (
        <button type="button" className="panel-sok-tom" onClick={() => setSok('')} aria-label="Tøm søket">
          <Ikon navn="close-circle-outline" />
        </button>
      ) : null}
    </label>
  );

  const sokStatus =
    tolket === null ? (
      <p role="status" className="hb-dempet" data-testid="hb-sok-ugyldig">
        «{sok.trim()}» er ikke et nummer. Hypotesene har nummer som H-142.
      </p>
    ) : typeof tolket === 'number' && liste.isSuccess ? (
      <p role="status" className="hb-liten" data-testid="hb-sok-treff">
        {rader.length === 0 ? `Fant ingen hypotese med nummer H-${tolket}.` : `${rader.length} treff for «${sok.trim()}»`}
      </p>
    ) : null;

  // P10a: innboksen UTEN filter er tom — da er tomruten hele flaten, med tall som er sanne. Et rom
  // uten én hypotese er ikke «alle har en avgjørelse» (sett i drift 2026-10-04): da sier setningen
  // det, og den fylte knappen går dit noe kan skje — en ny hypotese, ikke en tom «Til deg».
  const romTomt = c !== undefined && antallIRommet(c) === 0;
  const velger = <RekkefolgeVelger sort={sort} onVelg={setSort} />;
  const tom =
    nr === null && tomUtenFilter ? (
      <div className="hb-tom" data-testid="hb-tom">
        <h1 className="panel-h1">Ingenting å vurdere</h1>
        {c ? <p className="hb-dempet">{romTomt ? 'Ingen hypoteser er sendt inn ennå.' : `Alle hypoteser har en avgjørelse. ${c.active} er valgt eller testes, og ${c.parked} er parkert.`}</p> : null}
        <div>
          <Link to={romTomt ? '/h/ny' : H_TIL_DEG.sti} className="panel-knapp panel-tomrute-knapp">
            {romTomt ? 'Ny hypotese' : H_TIL_DEG.navn}
            <Ikon navn="chevron-forward-outline" />
          </Link>
        </div>
      </div>
    ) : null;

  /*
    Under 900 px er toppen ÉN blokk, som R4.4 ga innboksen i rommet Innspill: tittelen, søket som
    ikon og rekkefølgen på én rad, tellerne under. Som kolonne (tittel, tre tellere på to rader,
    søkefeltet, rekkefølgen) tok toppen 304 px og kromet 51 % av 800 px (h23-harness 2026-10-16).
    Rekkefølgen tar sporets plass på tittelraden — rommet har ikke spor, og den er rommets ene
    segment. Over grensen er toppen uendret.
  */
  return (
    <Arbeidsflate
      tittel="Innboks"
      base="/h/innboks"
      nr={nr}
      kartTittel="Innboks · Hypoteser"
      grupper={tolket === null ? [] : [{ navn: null, rader }]}
      laster={liste.isPending && tolket !== null}
      feil={
        liste.isError ? (
          <Varsel handling={<Tekstknapp onClick={() => void liste.refetch()}>Prøv igjen</Tekstknapp>}>Kunne ikke hente innboksen. Ingenting er endret.</Varsel>
        ) : filter !== 'alle' && liste.isSuccess && rader.length === 0 && tolket === undefined ? (
          <p role="status" className="hb-dempet">
            Ingen hypoteser med dette filteret. <Tekstknapp onClick={() => setFilter('alle')}>Vis alle</Tekstknapp>
          </p>
        ) : null
      }
      tittelRad={
        smal ? (
          <>
            <button type="button" className="panel-sok-knapp" aria-pressed={sokAapen} aria-label="Søk etter nummer" aria-keyshortcuts="/" onClick={vekslSok} data-testid="hb-sok-knapp">
              <Ikon navn="search-outline" />
            </button>
            {tolket === undefined ? velger : null}
          </>
        ) : undefined
      }
      onSok={smal ? aapneSok : undefined}
      topp={
        <>
          {smal ? (sokAapen ? sokFelt : null) : null}
          {tellere}
          {smal ? null : sokFelt}
          {!smal && tolket === undefined ? velger : null}
          {sokStatus}
          {tolket === undefined ? (
            <NyeStripe antall={nye.antall} flere={nye.flere} sort={sort} entall="ny hypotese" flertall="nye hypoteser" onVis={() => (sort === 'nyest' ? void liste.refetch() : setSort('nyest'))} />
          ) : null}
        </>
      }
      tom={tom}
    />
  );
}

/**
 * Finnes det én hypotese i rommet (andres utkast telles aldri, H20)? Bare null betyr noe her, så
 * overlapp mellom tellerne (konkludert telles også per utfall) spiller ingen rolle.
 */
function antallIRommet(c: HypothesisCounts): number {
  return c.received + c.reviewing + c.parked + c.active + c.paused + c.confirmed + c.partial + c.refuted + c.stopped + c.rejected + c.withdrawn;
}

function TellerKnapp({ paa, onClick, tall, ord, varsel }: { paa: boolean; onClick: () => void; tall: number; ord: string; varsel?: boolean }) {
  return (
    <button type="button" className={varsel ? 'hb-teller hb-teller-varsel' : 'hb-teller'} aria-pressed={paa} onClick={onClick} aria-label={`${tall} ${ord}`} data-testid={`hb-teller-${ord.split(' ')[0]}`}>
      {varsel ? <Ikon navn="warning-outline" /> : null}
      <b>{tall}</b>
      <span>{ord}</span>
    </button>
  );
}
